import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Truck, Package, AlertTriangle, Clock, Trash2 } from 'lucide-react'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { ActionMenu } from '../../components/common/ActionMenu'
import { CancelPurchaseModal } from '../../components/purchases/CancelPurchaseModal'
import { useCurrency } from '../../hooks/useCurrency'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { displayDate } from '../../utils/dates'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import { resolveContactName } from '../../lib/accountingService'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

type POItem = {
  id: string
  product_id: string
  product_name: string
  quantity: number
  quantity_received: number
  unit_price: number
  total: number
  products: { id: string; name: string; sku: string | null } | null
}

type TransitPO = {
  id: string
  order_number: string
  created_at: string
  expected_delivery_date: string | null
  status: string
  delivery_status: string | null
  total: number
  amount_paid: number
  payment_status: string
  supplier_id: string | null
  quantity_received: number
  quantity_in_transit: number
  suppliers: { id: string; name: string; business_name?: string | null; phone?: string | null } | null
  purchase_order_items: POItem[]
}

type ReceiveItem = {
  itemId: string
  productId: string
  productName: string
  ordered: number
  prevReceived: number
  availableToReceive: number
  unitCost: number
}

function getDaysStatus(expectedDate: string | null): { label: string; className: string } {
  if (!expectedDate) return { label: 'No date', className: 'text-gray-400' }
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const expected = new Date(expectedDate); expected.setHours(0, 0, 0, 0)
  const diff = Math.round((expected.getTime() - today.getTime()) / 86400000)
  if (diff < 0) return { label: `${Math.abs(diff)} day${Math.abs(diff) !== 1 ? 's' : ''} overdue`, className: 'text-red-600 font-semibold' }
  if (diff === 0) return { label: 'Due today', className: 'text-amber-600 font-semibold' }
  return { label: `In ${diff} day${diff !== 1 ? 's' : ''}`, className: 'text-green-600' }
}

// ─── Receive Modal ─────────────────────────────────────────────────────────────

function ReceiveModal({ po, onClose, onSuccess }: {
  po: TransitPO; onClose: () => void; onSuccess: () => void
}) {
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canReceive = can('purchases.receive')
  const { tenant, user } = useAuthStore()
  const tid = tenant?.id ?? ''
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10))
  const [waybillNumber, setWaybillNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Actual quantity received per line (keyed by item id). Defaults to the amount
  // expected (availableToReceive); the buyer edits it to what physically arrived.
  const [actuals, setActuals] = useState<Record<string, number>>({})
  // Two-step gate: when any line differs from expected, the buyer must see the
  // difference note and explicitly confirm before the variance posts.
  const [confirmingVariance, setConfirmingVariance] = useState(false)

  const supplierName = resolveContactName(po.suppliers, 'Supplier')

  const items: ReceiveItem[] = po.purchase_order_items
    .map(i => {
      const prevReceived = i.quantity_received || 0
      const availableToReceive = Number(i.quantity) - prevReceived
      return {
        itemId: i.id,
        productId: i.product_id,
        productName: i.product_name || i.products?.name || 'Product',
        ordered: i.quantity,
        prevReceived,
        availableToReceive,
        unitCost: i.unit_price,
      }
    })
    .filter(i => i.availableToReceive > 0)

  // Per-line actual received + difference vs expected.
  const rows = items.map(i => {
    const raw = actuals[i.itemId]
    const actual = raw == null || Number.isNaN(raw) ? i.availableToReceive : Math.max(0, raw)
    return { ...i, actual, diff: actual - i.availableToReceive }
  })
  const variances = rows.filter(r => r.diff !== 0)
  const hasVariance = variances.length > 0

  const totalReceivingValue = rows.reduce((s, r) => s + r.actual * r.unitCost, 0)
  const totalReceivingUnits = rows.reduce((s, r) => s + r.actual, 0)
  const orderedValue = rows.reduce((s, r) => s + r.availableToReceive * r.unitCost, 0)
  // AP variance: expected value − actual value. > 0 short (payable reduced),
  // < 0 over (payable increased). Mirrors the RPC's plug, for the preview note.
  const apVariance = orderedValue - totalReceivingValue

  const setActual = (itemId: string, value: string) => {
    setConfirmingVariance(false)
    setActuals(prev => ({ ...prev, [itemId]: value === '' ? NaN : Number(value) }))
  }

  async function handleConfirmReceipt() {
    if (!canReceive) { toast.error('Not authorized to receive goods'); return }
    if (items.length === 0) { toast.error('Nothing left to receive on this PO'); return }

    // Variance requires an explicit acknowledgement of the difference first.
    if (hasVariance && !confirmingVariance) { setConfirmingVariance(true); return }

    setSubmitting(true)
    try {
      // Exact match → the unchanged mig-372 receiver. Any difference → the
      // additive variance receiver, which posts inventory at the ACTUAL quantity
      // and routes the money difference through the supplier's AP control.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = hasVariance
        ? await (supabase as any).rpc('receive_goods_in_transit_variance_atomic', {
            p_tenant_id: tid,
            p_purchase_order_id: po.id,
            p_lines: rows.map(r => ({ poi_id: r.itemId, actual_qty: r.actual })),
            p_receive_date: receiptDate,
            p_received_by: user?.id ?? null,
          })
        : await (supabase as any).rpc('receive_goods_in_transit_atomic', {
            p_tenant_id: tid,
            p_purchase_order_id: po.id,
            p_receive_date: receiptDate,
            p_received_by: user?.id ?? null,
          })
      if (error) throw error

      // Audit record (waybill / notes / receipt number). Non-fatal — the
      // atomic RPC has already committed stock + journal.
      try {
        const { count } = await supabase
          .from('purchase_receipts')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tid)
        const receiptNum = `PR-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String((count ?? 0) + 1).padStart(4, '0')}`

        const { data: receipt } = await db('purchase_receipts').insert({
          tenant_id: tid,
          purchase_order_id: po.id,
          receipt_number: receiptNum,
          receipt_date: receiptDate,
          waybill_number: waybillNumber || null,
          notes: notes || null,
          total_value: totalReceivingValue,
          received_by: user?.id ?? null,
        }).select('id').single()

        if (receipt?.id) {
          await db('purchase_receipt_items').insert(
            rows.map(r => ({
              tenant_id: tid,
              receipt_id: receipt.id,
              purchase_order_item_id: r.itemId,
              product_id: r.productId,
              product_name: r.productName,
              quantity_ordered: r.ordered,
              quantity_previously_received: r.prevReceived,
              quantity_received: r.actual,
              quantity_rejected: Math.max(0, r.availableToReceive - r.actual),
              rejection_reason: null,
              unit_cost: r.unitCost,
              total_value: r.actual * r.unitCost,
            }))
          )
        }
      } catch (auditErr) {
        console.warn('Receipt audit record failed (stock + journal already posted):', auditErr)
      }

      const result = data as { total_value?: number } | null
      const postedValue = result?.total_value ?? totalReceivingValue
      toast.success(`Goods received! ${totalReceivingUnits} unit${totalReceivingUnits !== 1 ? 's' : ''} added to stock. Journal posted (${postedValue ? format(Number(postedValue)) : ''}).`)
      onSuccess()
      onClose()
    } catch (err) {
      toast.error('Receipt failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Receive Goods" size="xl">
      <div className="space-y-4">
        {/* Header info */}
        <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Purchase Order</p>
            <p className="text-sm font-bold text-gray-900">{po.order_number}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Supplier</p>
            <p className="text-sm font-medium text-gray-700 truncate">{supplierName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total Value</p>
            <p className="text-sm font-bold text-orange-600">{format(po.total)}</p>
          </div>
        </div>

        {/* Receipt details - 3 columns */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Date Received *</label>
            <input
              type="date"
              value={receiptDate}
              onChange={e => setReceiptDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">When did the goods arrive?</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Delivery Waybill</label>
            <input
              type="text"
              value={waybillNumber}
              onChange={e => setWaybillNumber(e.target.value)}
              placeholder="Optional"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional delivery notes"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Items table */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Items to Receive</p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: '480px' }}>
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-[40%]">Product</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 w-[12%]">Ordered</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 w-[20%]">Received</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 w-[13%]">Unit Cost</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 w-[15%]">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(item => (
                    <tr key={item.itemId} className="hover:bg-orange-50/30">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 leading-tight">{item.productName}</p>
                        {item.prevReceived > 0 && (
                          <p className="text-xs text-gray-400">{item.prevReceived} previously received of {item.ordered}</p>
                        )}
                        {item.diff !== 0 && (
                          <p className={cn('text-xs font-medium', item.diff < 0 ? 'text-amber-600' : 'text-blue-600')}>
                            {item.diff < 0
                              ? `Short ${Math.abs(item.diff)}`
                              : `Over ${item.diff}`} vs ordered {item.availableToReceive}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-sm text-gray-500">{item.availableToReceive}</td>
                      <td className="px-3 py-3 text-center">
                        <input
                          type="number" min="0" step="1"
                          value={actuals[item.itemId] == null || Number.isNaN(actuals[item.itemId]) ? '' : actuals[item.itemId]}
                          placeholder={String(item.availableToReceive)}
                          onChange={e => setActual(item.itemId, e.target.value)}
                          className={cn(
                            'w-20 text-center border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-orange-400',
                            item.diff !== 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200',
                          )}
                        />
                      </td>
                      <td className="px-3 py-3 text-right text-sm text-gray-700">{format(item.unitCost)}</td>
                      <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">{format(item.actual * item.unitCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Summary + Journal side by side on wide modal */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Summary</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-blue-700">Receiving (actual)</span>
                <span className="font-semibold text-blue-900">{totalReceivingUnits} units</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-700">Value</span>
                <span className="font-semibold text-blue-900">{format(totalReceivingValue)}</span>
              </div>
              <p className="text-xs text-blue-600 pt-1 border-t border-blue-200">
                {hasVariance
                  ? 'Variance receipt — stock posts at the actual quantity; the difference adjusts the supplier balance.'
                  : 'Full receipt — stock, ledger and journal post atomically.'}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Journal Entry</p>
            <div className="text-xs text-gray-700 space-y-2 font-mono">
              <div>
                <span className="text-green-700 font-semibold">DR</span>
                <span className="ml-2">Stock on Hand (1201)</span>
                <div className="text-right font-semibold text-gray-900 mt-0.5">{format(totalReceivingValue)}</div>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <span className="text-red-700 font-semibold">CR</span>
                <span className="ml-2">Goods in Transit (1202)</span>
                <div className="text-right font-semibold text-gray-900 mt-0.5">{format(orderedValue)}</div>
              </div>
              {Math.abs(apVariance) > 0.005 && (
                <div className="border-t border-gray-200 pt-2">
                  <span className={cn('font-semibold', apVariance > 0 ? 'text-green-700' : 'text-red-700')}>
                    {apVariance > 0 ? 'DR' : 'CR'}
                  </span>
                  <span className="ml-2">Accounts Payable — {supplierName}</span>
                  <div className="text-right font-semibold text-gray-900 mt-0.5">{format(Math.abs(apVariance))}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {apVariance > 0 ? 'Short — reduces what we owe (may become an advance).' : 'Over — increases what we owe.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Difference note + explicit confirmation gate */}
        {hasVariance && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">What arrived differs from what was ordered</p>
                <ul className="mt-1.5 space-y-1">
                  {variances.map(v => (
                    <li key={v.itemId} className="text-xs text-amber-800">
                      <span className="font-medium">{v.productName}</span>: ordered {v.availableToReceive}, received {v.actual} —{' '}
                      {v.diff < 0 ? `short ${Math.abs(v.diff)}` : `over ${v.diff}`}{' '}
                      ({format(Math.abs(v.diff) * v.unitCost)})
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-700">
                  {apVariance > 0
                    ? `Net short ${format(apVariance)} — the supplier's payable will be reduced (a prepayment/advance if it exceeds what's owed).`
                    : `Net over ${format(Math.abs(apVariance))} — the supplier's payable will be increased.`}
                </p>
                {confirmingVariance && (
                  <p className="mt-2 text-sm font-bold text-amber-900">This is the difference. Do you want to continue?</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={confirmingVariance ? () => setConfirmingVariance(false) : onClose}
            className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
            {confirmingVariance ? 'Back' : 'Cancel'}
          </button>
          <button
            onClick={handleConfirmReceipt}
            disabled={submitting || (totalReceivingUnits === 0 && !hasVariance)}
            className={cn(
              'flex-2 px-6 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed',
              hasVariance ? 'bg-amber-600 hover:bg-amber-700' : 'bg-orange-500 hover:bg-orange-600',
            )}>
            {submitting
              ? 'Processing...'
              : !hasVariance
                ? `Confirm Receipt — ${totalReceivingUnits} unit${totalReceivingUnits !== 1 ? 's' : ''} · ${format(totalReceivingValue)}`
                : confirmingVariance
                  ? 'Yes, post with difference'
                  : 'Review difference & continue'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function GoodsInTransitPage() {
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canReceive = can('purchases.receive')
  const { tenant, user } = useAuthStore()
  const tid = tenant?.id ?? ''
  const qc = useQueryClient()

  const [receivingPO, setReceivingPO] = useState<TransitPO | null>(null)
  const [cancellingPO, setCancellingPO] = useState<TransitPO | null>(null)

  const { data: orders = [], isLoading } = useQuery<TransitPO[]>({
    queryKey: ['transit-pos', tid],
    enabled: !!tid,
    queryFn: async () => {
      const { data, error } = await db('purchase_orders')
        .select(`
          id, order_number, created_at, expected_delivery_date, status, delivery_status,
          total, amount_paid, payment_status, supplier_id,
          quantity_received, quantity_in_transit,
          suppliers (id, name, business_name, phone),
          purchase_order_items (
            id, product_id, product_name, quantity, quantity_received,
            unit_price, total, products (id, name, sku)
          )
        `)
        .eq('tenant_id', tid)
        .eq('delivery_status', 'in_transit')
        .neq('status', 'cancelled')
        .order('expected_delivery_date', { ascending: true })
      if (error) throw error
      return (data ?? []) as TransitPO[]
    },
    staleTime: 30 * 1000,
  })

  const today = new Date(); today.setHours(0, 0, 0, 0)
  const overdueCount = orders.filter(o => {
    if (!o.expected_delivery_date) return false
    const d = new Date(o.expected_delivery_date); d.setHours(0, 0, 0, 0)
    return d < today
  }).length

  const dueThisWeek = orders.filter(o => {
    if (!o.expected_delivery_date) return false
    const d = new Date(o.expected_delivery_date); d.setHours(0, 0, 0, 0)
    const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7)
    return d >= today && d <= weekEnd
  }).length

  const totalValue = orders.reduce((s, o) => s + o.total, 0)

  function onDone() {
    qc.invalidateQueries({ queryKey: ['transit-pos'] })
    qc.invalidateQueries({ queryKey: ['purchase-orders-enhanced'] })
    qc.invalidateQueries({ queryKey: ['dashboard-transit-count'] })
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card py-3">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium text-gray-500">In Transit</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{orders.length}</p>
          <p className="text-xs text-gray-400">purchase orders</p>
        </div>
        <div className="card py-3">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-orange-500" />
            <span className="text-xs font-medium text-gray-500">Total Value</span>
          </div>
          <p className="text-xl font-bold text-gray-900 mt-1">{format(totalValue)}</p>
          <p className="text-xs text-gray-400">outstanding</p>
        </div>
        <div className="card py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn('h-4 w-4', overdueCount > 0 ? 'text-red-500' : 'text-gray-300')} />
            <span className="text-xs font-medium text-gray-500">Overdue</span>
          </div>
          <p className={cn('text-2xl font-bold mt-1', overdueCount > 0 ? 'text-red-600' : 'text-gray-400')}>
            {overdueCount}
          </p>
          <p className="text-xs text-gray-400">past expected date</p>
        </div>
        <div className="card py-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium text-gray-500">Due This Week</span>
          </div>
          <p className="text-2xl font-bold text-amber-600 mt-1">{dueThisWeek}</p>
          <p className="text-xs text-gray-400">arriving soon</p>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : orders.length === 0 ? (
        <EmptyState
          title="No goods in transit"
          description="When you mark a purchase as 'In Transit', it will appear here until goods are received."
          icon={<Truck className="h-8 w-8" />}
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['', 'PO #', 'Supplier', 'Date Ordered', 'Expected Receiving', 'Days', 'Items', 'Value', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(o => {
                  const days = getDaysStatus(o.expected_delivery_date)
                  const itemCount = o.purchase_order_items.length
                  const supplierName = o.suppliers?.business_name || o.suppliers?.name || '—'
                  const hasPartialReceipt = o.purchase_order_items.some(i => (i.quantity_received ?? 0) > 0 && (i.quantity_received ?? 0) < i.quantity)
                  return (
                    <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {canReceive && (
                            <button
                              onClick={() => setReceivingPO(o)}
                              className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium whitespace-nowrap"
                            >
                              Receive Goods
                            </button>
                          )}
                          <ActionMenu
                            items={[
                              {
                                label: 'Cancel PO',
                                icon: <Trash2 className="h-3.5 w-3.5" />,
                                onClick: () => setCancellingPO(o),
                                variant: 'danger',
                              },
                            ]}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{o.order_number}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{supplierName}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{displayDate(o.created_at)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {o.expected_delivery_date ? displayDate(o.expected_delivery_date) : '—'}
                      </td>
                      <td className={cn('px-4 py-3 text-xs whitespace-nowrap', days.className)}>{days.label}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{itemCount} item{itemCount !== 1 ? 's' : ''}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900 text-right whitespace-nowrap">{format(o.total)}</td>
                      <td className="px-4 py-3">
                        <span className={cn('badge', hasPartialReceipt ? 'badge-orange' : 'badge-blue')}>
                          {hasPartialReceipt ? '⚠ Partially received' : '🚚 In Transit'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            {orders.length} order{orders.length !== 1 ? 's' : ''} in transit
          </div>
        </div>
      )}

      {receivingPO && (
        <ReceiveModal po={receivingPO} onClose={() => setReceivingPO(null)} onSuccess={onDone} />
      )}
      <CancelPurchaseModal
        open={!!cancellingPO}
        onClose={() => setCancellingPO(null)}
        po={cancellingPO ? {
          id: cancellingPO.id,
          order_number: cancellingPO.order_number,
          total: cancellingPO.total,
          amount_paid: cancellingPO.amount_paid,
          payment_status: cancellingPO.payment_status,
          status: cancellingPO.status,
          delivery_status: cancellingPO.delivery_status,
          supplierName: cancellingPO.suppliers?.business_name?.trim() || cancellingPO.suppliers?.name?.trim() || undefined,
        } : null}
        tenantId={tid}
        userId={user?.id}
        onSuccess={onDone}
      />
    </div>
  )
}
